import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Typography, notification } from 'antd';
import {
  StyledExamConfigForm,
  StyledFormItem,
  StyledConfCornerForm,
  StyledPrimaryButton,
  StyledFormItemSummary,
} from '../../../../common/styled';
import { SuffixIcon } from '@med-fe/ui';
import { useDispatch, useSelector } from 'react-redux';
import { getExamEndTimeByName, getExamsNameCount, getTemplateDetails } from '../../../templates-selector';
import { TEMPLATES_MANAGEMENT_STATE_KEY } from '../../../templates-selector';
import {
  setAddedExam,
  setCanAddExam,
  fetchExamTypes,
  setEditedExam,
  removeSlotFromSession,
} from '../../../templates-actions';

const { Title } = Typography;

interface NewExaminationProps {
  data: any;
  onInsert: (values: any) => void;
  onClose: () => void;
  isEdit?: boolean;
  isSpecialRange?: boolean;
  isRange0?: boolean;
}

// Style override for input height
const inputHeightOverride = `
  .ant-form-item-control .ant-form-item-control-input .ant-input-affix-wrapper input {
    height: 21px !important;
  }
`;

export const NewExamination: React.FC<NewExaminationProps> = ({
  data,
  onInsert,
  onClose,
  isEdit = false,
  isSpecialRange = false,
  isRange0 = false,
}) => {
  // Helper to generate time options for dropdown (every 5 min for 1 hour)
  const getTimeOptions = (baseTime: string): { value: string; label: string }[] => {
    let hour = 8,
      minutes = 0,
      period = 'AM';
    if (baseTime && isValidTime(baseTime)) {
      const [time, p] = baseTime.split(' ');
      period = p;
      const [h, m] = time.split(':');
      hour = parseInt(h, 10);
      minutes = parseInt(m, 10);
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
    }
    const options: { value: string; label: string }[] = [];
    for (let i = 0; i < 60; i += 5) {
      let totalMinutes = hour * 60 + i;
      let h = Math.floor(totalMinutes / 60);
      let m = totalMinutes % 60;
      let ampm = h >= 12 ? 'PM' : 'AM';
      let displayHour = h % 12;
      displayHour = displayHour === 0 ? 12 : displayHour;
      options.push({
        value: `${displayHour}:${m.toString().padStart(2, '0')} ${ampm}`,
        label: `${displayHour}:${m.toString().padStart(2, '0')} ${ampm}`,
      });
    }
    return options;
  };
  const dispatch = useDispatch();
  const [examData, setExamData] = useState(data);
  const [durationLabel, setDurationLabel] = useState('Duration of Exam (In Minutes)');
  const [examTypeLabel, setExamTypeLabel] = useState('Exam Type');
  const [startTimeError, setStartTimeError] = useState('');
  const [durationError, setDurationError] = useState('');
  const templateDetails = useSelector(getTemplateDetails);

  const examTypesFromRedux = useSelector((state: any) => state[TEMPLATES_MANAGEMENT_STATE_KEY]?.examTypes);
  let examTypes = examTypesFromRedux || [];

  // Filter out LUNCH if isSpecialRange or isRange0 is true
  if (isSpecialRange || isRange0) {
    examTypes = examTypes.filter((type) => {
      const examType = typeof type === 'string' ? type : type.ExamType || type.examType || type.name || type.value;
      return examType?.toLowerCase() !== 'lunch';
    });
  }

  // Fetch exam types from API if not loaded
  useEffect(() => {
    // Only fetch if examTypesFromRedux is strictly null (not undefined, not empty array)
    if (examTypesFromRedux === null) {
      dispatch(fetchExamTypes());
    }
  }, [examTypesFromRedux, dispatch]);

  // Keep local state in sync with Redux/parent data
  useEffect(() => {
    setExamData(data);
  }, [data]);
  //const examData = data;
  const [form] = Form.useForm();
  const count = useSelector((state) => getExamsNameCount(state, examData.examName));
  const addedExam = useSelector((state: any) => state[TEMPLATES_MANAGEMENT_STATE_KEY]?.addedExam);

  // Calculate end time from start and duration
  const getCalculatedEndTime = () => {
    const start = examData.from;
    const duration = Number(examData.durationOfExam);
    if (!start || !duration || isNaN(duration)) return '';
    const todayStr = new Date().toISOString().slice(0, 10);
    const startDate = new Date(`${todayStr} ${start}`);
    const endDate = new Date(startDate.getTime() + duration * 60000);
    // Format as hh:mm AM/PM
    let hours = endDate.getHours();
    const minutes = endDate.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)} ${ampm}`;
  };
  const endTime = getCalculatedEndTime();

  useEffect(() => {
    if (examData) {
      form.setFieldsValue({ ...examData, examNumber: count });
    }
  }, [examData]);

  useEffect(() => {
    // Clear startTimeError when a new slot is selected
    setStartTimeError('');
    setDurationError('');
  }, [addedExam]);

  // Helper to validate time in HH:MM AM/PM format
  const isValidTime = (value) => {
    if (!value) return false;
    // Accepts only 12-hour format with AM/PM (e.g., 1:00 AM, 12:59 PM)
    return /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM|am|pm)$/.test(value.trim());
  };

  // Helper to validate durationOfExam: must be a number > 0
  const isValidDurationOfExam = (value) => {
    const num = Number(value);
    return !isNaN(num) && num > 0 && num <= 480;
  };

  const handleChange = (e) => {
    let updated = { ...examData, [e.target.name]: e.target.value };
    if (e.target.name === 'examType') {
      updated.examName = e.target.value;
      // Update label based on selection
      if (
        (typeof e.target.value === 'string' && e.target.value === 'Lunch') ||
        (typeof e.target.value === 'object' && (e.target.value.ExamType || e.target.value.examType) === 'Lunch')
      ) {
        setDurationLabel('Duration of Lunch (In Minutes)');
        setExamTypeLabel('Lunch');
      } else {
        setDurationLabel('Duration of Exam (In Minutes)');
        setExamTypeLabel('Exam Type');
      }
    }
    // Always store durationOfExam as a number
    if (e.target.name === 'durationOfExam') {
      updated.durationOfExam = Number(e.target.value);
    }
    // Always recalculate 'to' (endTime) if 'from' or 'durationOfExam' changes
    if (e.target.name === 'from' || e.target.name === 'durationOfExam') {
      const start = e.target.name === 'from' ? e.target.value : updated.from;
      const duration = e.target.name === 'durationOfExam' ? Number(e.target.value) : updated.durationOfExam;
      if (start && duration && !isNaN(duration)) {
        const todayStr = new Date().toISOString().slice(0, 10);
        const startDate = new Date(`${todayStr} ${start}`);
        const endDate = new Date(startDate.getTime() + duration * 60000);
        const pad = (n) => n.toString().padStart(2, '0');
        let hours = endDate.getHours();
        const minutes = endDate.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        updated.to = `${pad(hours)}:${pad(minutes)} ${ampm}`;
      } else {
        updated.to = '';
      }
    }
    setExamData(updated);
    if (isEdit && updated.examId) {
      // Mark the edited exam in Redux so other parts can react.
      // Do NOT remove the slot from templateDetails here — removal should happen
      // when the edit is initiated (so MainInfo.update won't accidentally persist a removal).
      dispatch(setEditedExam(updated));
    }
    dispatch(setAddedExam(updated));
  };

  // Helper to format hour as AM/PM
  const toAmPm = (hour) => {
    const h = ((hour - 1) % 12) + 1;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h}:00 ${ampm}`;
  };

  const normalizeHour = (timeStr: string, isEnd: boolean, startRaw?: string) => {
    if (!timeStr) return '';

    let hour = 0;
    let minutes = 0;
    let period: 'AM' | 'PM' = 'AM';
    const raw = timeStr.trim();

    // Parse hour and minutes
    if (raw.includes(':')) {
      const [h, m] = raw.split(':');
      hour = parseInt(h, 10);
      minutes = parseInt(m, 10);
    } else {
      if (raw.length <= 2) {
        hour = parseInt(raw, 10);
        minutes = 0;
      } else {
        hour = parseInt(raw.slice(0, -2), 10);
        minutes = parseInt(raw.slice(-2), 10);
      }
    }

    if (!isEnd) {
      // Start time rules: 7–11 AM, else PM
      if (hour >= 7 && hour <= 11) {
        period = 'AM';
      } else {
        period = 'PM';
      }
    } else {
      // End time rules
      const startHour = startRaw ? parseInt(startRaw, 10) : 0;

      if (startHour >= 7 && startHour <= 11) {
        // Start in AM range
        // Use > instead of >= to handle same-hour cases (e.g., "8-8" means 8 AM to 8 PM)
        if (hour > startHour && hour <= 11) {
          period = 'AM'; // same AM block
        } else {
          period = 'PM'; // otherwise PM (including same hour)
        }
      } else {
        // Start is PM → End is always PM unless wrapping
        period = 'PM';
      }
    }

    // Convert to 12-hour format
    if (hour === 0) hour = 12;
    if (hour > 12) hour -= 12;

    return `${hour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Compute hooPart from template name as fallback if not provided by API
  const computedHooPart = (() => {
    const templateName = templateDetails?.templateName || '';
    const firstSpaceIdx = templateName.indexOf(' ');
    if (firstSpaceIdx > 0) {
      return templateName.substring(0, firstSpaceIdx);
    }
    return templateName;
  })();

  const hooPart1 = templateDetails?.hooPart || computedHooPart || '';
  const [startRaw, endRaw] = hooPart1.split('-');

  let hooStart = normalizeHour(startRaw, false);
  let hooEnd = normalizeHour(endRaw, true, startRaw);

  const handleInsert = async () => {
    // Show errors if present and prevent submit
    if (startTimeError || durationError) {
      setStartTimeError(!isValidTime(examData.from) ? 'Please enter valid Start Time' : '');
      setDurationError(
        !isValidDurationOfExam(examData.durationOfExam) ? 'Please enter a valid duration greater than 0' : ''
      );
      return;
    }
    try {
      const values = await form.validateFields();
      // Always include examName from examData for correct count
      const start = values.from;
      const duration = Number(values.durationOfExam);
      let to = values.to;
      if (start && duration && !isNaN(duration)) {
        const todayStr = new Date().toISOString().slice(0, 10);
        const startDate = new Date(`${todayStr} ${start}`);
        const endDate = new Date(startDate.getTime() + duration * 60000);
        // Calculate to if not provided
        if (!to) {
          const pad = (n) => n.toString().padStart(2, '0');
          let hours = endDate.getHours();
          const minutes = endDate.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12;
          hours = hours ? hours : 12;
          to = `${pad(hours)}:${pad(minutes)} ${ampm}`;
        }
        // --- Hours of Operation Validation ---
        const templateName = templateDetails?.templateName || '';
        // Accept formats: 7-2, 730-230, 8-3, etc.
        const hoursMatch = templateName.match(/(\d{1,2})(\d{2})?-(\d{1,2})(\d{2})?/);
        if (hoursMatch) {
          let [, startHourStr, startMinStr, endHourStr, endMinStr] = hoursMatch;
          let opStart = new Date(`${todayStr} ${hooStart}`);
          let opEnd = new Date(`${todayStr} ${hooEnd}`);

          // Validate slot is within HOO and HOO is within allowed range
          if (startDate < opStart || endDate > opEnd) {
            notification.warning({
              message: 'Slot Outside Hours of Operation',
              description: `Slot must be between ${hooStart} and ${hooEnd} as per template name (${templateName}).`,
              placement: 'topRight',
            });
            return;
          }
        }
        // --- Slot Overlap Validation ---
        const examsConfiguration = templateDetails?.examsConfiguration || [];
        const filteredExams =
          isEdit && examData?.examId
            ? examsConfiguration.filter((item) => item.examId !== examData.examId)
            : examsConfiguration;
        const hasConflict = filteredExams.some((item) => {
          const eventStart = new Date(`${todayStr} ${item.from}`);
          const eventEnd = new Date(`${todayStr} ${item.to}`);
          return (
            (startDate >= eventStart && startDate < eventEnd) ||
            (endDate > eventStart && endDate <= eventEnd) ||
            (startDate <= eventStart && endDate >= eventEnd)
          );
        });
        if (hasConflict) {
          notification.warning({
            message: 'Slot Overlap',
            description:
              'The selected start time and duration overlap with an existing slot. Please choose a different time.',
            placement: 'topRight',
          });
          return;
        }
      }
      // Ensure examId is set for new slots
      let examId = examData.examId;
      if (!examId) {
        // Generate a new examId (could use timestamp or max+1 from existing)
        const examsConfiguration = templateDetails?.examsConfiguration || [];
        examId = examsConfiguration.length > 0 ? Math.max(...examsConfiguration.map((e) => e.examId || 0)) + 1 : 1;
      }
      onInsert({
        ...values,
        examName: examData.examName,
        to,
        durationOfMinutes: duration,
        durationOfExam: String(duration),
        examId,
      });
    } catch (err) {
      // validation failed
    }
  };

  return (
    <>
      <style>{inputHeightOverride}</style>
      <StyledExamConfigForm form={form} layout='vertical' className='search-container'>
        <StyledFormItem
          key='from'
          name='from'
          label='Start Time'
          style={{ width: 180 }}
          validateStatus={startTimeError ? 'error' : ''}
          help={startTimeError || ''}
        >
          <Select
            showSearch
            placeholder='Select Start Time'
            value={examData.from || undefined}
            onChange={(value) => {
              if (!isValidTime(value)) {
                setStartTimeError('Please enter valid Start Time');
                setExamData({ ...examData, from: value });
                return;
              } else {
                setStartTimeError('');
              }
              handleChange({ target: { name: 'from', value } });
            }}
            options={getTimeOptions(examData.from || '8:00 AM')}
            style={{ width: '100%' }}
          />
        </StyledFormItem>
        <StyledFormItem
          key='durationOfExam'
          name='durationOfExam'
          label={durationLabel}
          style={{ width: 180 }}
          validateStatus={durationError ? 'error' : ''}
          help={durationError || ''}
        >
          <Input
            allowClear
            placeholder='0'
            name='durationOfExam'
            value={examData.durationOfExam || ''}
            onChange={(e) => {
              // Validate on change for Duration of Exam
              if (!isValidDurationOfExam(e.target.value)) {
                setDurationError('Please enter a valid duration (1-480 minutes)');
                setExamData({ ...examData, durationOfExam: e.target.value });
                return;
              } else {
                setDurationError('');
              }
              handleChange(e);
            }}
          />
        </StyledFormItem>
        <StyledFormItem key='examType' name='examType' label={examTypeLabel} style={{ width: 180 }}>
          <Select
            // allowClear removed to make examType mandatory
            placeholder='Select Exam Type'
            value={isEdit ? examData?.examName : examData.examType || undefined}
            onChange={(value) => handleChange({ target: { name: 'examType', value } })}
            options={examTypes.map((type) =>
              typeof type === 'string'
                ? { value: type, label: type }
                : {
                    value: type.ExamType || type.examType || type.name || type.value,
                    label: type.ExamType || type.examType || type.name || type.value,
                  }
            )}
          />
        </StyledFormItem>
        <StyledConfCornerForm>
          <StyledFormItemSummary key='to' name='to' label='End Time:'>
            <Title level={5}>{endTime || '--:--'} </Title>
          </StyledFormItemSummary>
          <StyledPrimaryButton type={'primary'} onClick={handleInsert}>
            {isEdit ? 'Update' : 'Insert'}
          </StyledPrimaryButton>
          <StyledPrimaryButton type={'default'} onClick={onClose} style={{ width: 120, marginTop: 5 }}>
            Cancel
          </StyledPrimaryButton>
        </StyledConfCornerForm>
      </StyledExamConfigForm>
    </>
  );
};
